import { InfoPage } from '@/components/info-page.js';
import { InfoPageSuspenseBoundary } from '@/components/info-page-suspense-boundary.js';
import { axiosErrorFrom, airServerErrorFrom } from '@/lib/error-handling.js';
import { validateRouteRole } from '@/lib/routing.js';
import { isCoach, isEdgeCoach } from '@airreading/common';
import { createFileRoute } from '@tanstack/react-router';
import { paymentsTabs } from './-payments-tabs.js';
import { CardTitle } from '@/components/ui/card.js';
import { GuidelineTable } from './-components/guideline-table.js';
import { guidelineText } from './-components/guideline-text.js';
import { getCoachUpcomingPayments } from '@/lib/api-calls.js';
import { useQuery } from '@tanstack/react-query';
import { EntityTable } from '@/components/table/entity-table.js';
import { createColumnHelper, getCoreRowModel, useReactTable } from '@tanstack/react-table';
import { CoachPaymentReportDTO, PayCycleDTO } from '@airreading/common/data';
import { TableErrorState } from '@/components/table/table-error-state.js';

export const Route = createFileRoute('/_layout/payments/guideline')({
  beforeLoad: validateRouteRole((user) => isCoach(user) && !isEdgeCoach(user)),
  component: Page,
});

function Page() {
  return (
    <InfoPageSuspenseBoundary
      onError={(e) => {
        const axiosError = axiosErrorFrom(e);
        const airServerError = airServerErrorFrom(e);

        return {
          allowRetry: axiosError?.response?.status !== 404,
          errorHeader: airServerError?.userFacingMessage || 'Failed to load coach payments. Please try again.',
          back: {
            backLink: {
              to: '/',
            },
          },
        };
      }}
    >
      <CoachPaymentsPage />
    </InfoPageSuspenseBoundary>
  );
}

const columnHelper = createColumnHelper<CoachPaymentReportDTO>();

const columns = [
  columnHelper.accessor('payDate', {
    id: 'paymentDate',
    header: 'Pay initiation date',
    enableSorting: false,
  }),
  columnHelper.accessor('payCycleId', {
    id: 'payPeriod',
    header: 'Period',
    cell: ({ getValue }) => {
      const payCycleId = getValue();
      const period = PayCycleDTO.fromPayPeriodId(payCycleId).payPeriodInterval;
      return period;
    },
    enableSorting: false,
  }),
  columnHelper.accessor('depositPostedDate', {
    id: 'depositPostedDate',
    header: 'Delivery (1-3 business days)',
    enableSorting: false,
  }),
];

function CoachPaymentsPage() {
  const { data: upcomingPayments, isPending, refetch, error } = useQuery(getCoachUpcomingPayments());

  const table = useReactTable({
    data: upcomingPayments || [],
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div>
      <InfoPage headerText="Payments" tabs={paymentsTabs('Guideline')} data-testid="payments-page">
        <CardTitle className="text-xl font-bold flex items-center mb-4" data-testid="payments-guideline-upcoming-title">
          Upcoming payments
        </CardTitle>
        <EntityTable
          table={table}
          showPagination={false}
          isLoading={isPending}
          hasError={!!error}
          errorState={<TableErrorState refetchAction={refetch} />}
          hideEndMessage
          emptyState={'No payments found'}
          emptyStateClassName="translate-y-0"
          data-testid="payments-guideline-upcoming-table"
          rowTestId={(_, rowIndex) => `payments-guideline-upcoming-table-row-${rowIndex}`}
          cellTestId={(_, columnId, rowIndex) => `payments-guideline-upcoming-table-cell-${columnId}-${rowIndex}`}
        />

        <CardTitle className="text-xl font-bold mb-4" data-testid="payments-guideline-breakdown-title">
          Payment breakdown
        </CardTitle>
        <section className="mb-4" data-testid="payments-guideline-breakdown">
          {guidelineText.map((section) => (
            <GuidelineTable key={section.type} {...section} />
          ))}
        </section>
        <p data-testid="payments-guideline-additional-earnings">
          Additional earning opportunities including supporting curriculum and operations at Air Reading, referral
          incentives, and more.
        </p>
      </InfoPage>
    </div>
  );
}
